/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { ApplicationEvent } from '../../event/ApplicationEvent';
import { AdPlaybackEvent } from '../../event/AdPlaybackEvent';
import { PlaybackEvent } from '../../event/PlaybackEvent';
import { PlaybackDataKey } from '../../datakey/PlaybackDataKey';
import { AnalyticsReporterConfiguration } from '../configuration/AnalyticsReporterConfiguration';
import { GodavariAnalyticsReporterConfiguration } from '../configuration/GodavariAnalyticsReporterConfiguration';
import { PlaybackEndReason } from '../../model/PlaybackEndReason';
import { PlaybackStartupObservation } from '../../model/PlaybackStartupObservation';
import { isPlaybackStartupObservation } from '../util/PlaybackStartupObservationUtil';
import { toGodavariEndReason, toGodavariStartupObservation } from './godavari/GodavariStartupMapping';
import { Provider } from '../Provider';
import { AnalyticsReporter } from './model/AnalyticsReporter';
import {
  GodavariAdDetailType,
  GodavariBatchPayloads,
  GodavariConnectorPayloads,
  GodavariEmptyBatch,
  GodavariLifecyclePayloads,
  GodavariLifecyclePayload,
  GodavariPulseStage,
  GodavariPulseObservation,
  GodavariEndReason,
  GODAVARI_PULSE_STEPS,
  GODAVARI_PULSE_MICRO_STEPS,
  GODAVARI_PULSE_PLAYER_STATES,
  GodavariVideoDetailInput,
  GODAVARI_LIFECYCLE_HEADERS,
  GodavariObservationArgs,
  GodavariMetadataEvent,
  GodavariLifecycleObservation,
  GodavariMetadataInput,
  GodavariEventInput,
  GodavariEventMetadata,
} from './model/GodavariPayload';
import { normalizeGodavariEvent, pickGodavariMetadata } from '../util/GodavariMetadataUtil';
import { getOrCreateGodavariDeviceId } from '../util/GodavariDeviceIdUtil';
import { createGodavariSessionId, getOrCreateGodavariGvuid } from '../util/GodavariSessionIdUtil';
import {
  buildApplicationMetadata,
  buildApplicationPayload,
  buildCommonPayload,
  buildHeartbeatFields,
  buildBatchPayload,
  withGodavariErrorFields,
  getDeviceInformation,
  GodavariPayloadContext,
  toGodavariAdMetadata,
} from './godavari/GodavariPayloadBuilder';
import { GodavariMetadataStore } from './godavari/GodavariMetadataStore';
import { GodavariPulseController, resolveGodavariPulseConfiguration } from './godavari/GodavariPulseController';
import { GodavariHeartbeatTelemetry } from './godavari/GodavariHeartbeatTelemetry';
import { AdContentTimePosition } from '@quickplay/player-interface';
import type { NetworkRequestObservation } from '@quickplay/player-interface';
import { GodavariPlaybackState } from './godavari/GodavariPlaybackState';
import {
  emptyGodavariBatch,
  validateGodavariSdk,
  configureGodavariFrontend,
  startGodavariApplication,
  createGodavariConnector,
  registerGodavariCallbacks,
  closeGodavariSession,
  sendGodavariPayload,
  getGodavariApplicationSessionId,
  holdGodavariDelivery,
} from './godavari/GodavariSdk';
import {
  AD_DETAIL_EVENTS,
  STARTUP_PULSE_EVENTS,
  STARTUP_PLAYER_STATES,
  VIDEO_DETAIL_EVENTS,
  getNetworkPulseStage,
  canonicalGodavariEventName,
} from './godavari/GodavariEventMappings';

const TAG_NAME = 'FL_ANALYTICS_GODAVARI';
/**
 * The vendor drops these vsp fields only when they are explicitly "", and fills a missing key with "".
 * Setting "" for unset values keeps the beacon free of empty fields, as in SonyLIV.
 */
const VENDOR_OMIT_WHEN_EMPTY_FIELDS = [
  'video_entry_point',
  'subscribed_pack_name',
  'subscribed_pack_resolution',
  'max_entitled_video_resolution',
  'initial_video_setting',
  'updated_video_setting',
  'video_pt_sp_key',
  'video_end_reason',
  'video_playback_state',
  'video_ssai_id',
  'ssai_stitcher',
  'ga_session_id',
  'video_ssaias',
];

function omitUnsetOptionalFields(payload: object): void {
  const target: Record<string, unknown> = Object.assign(payload);
  for (const key of VENDOR_OMIT_WHEN_EMPTY_FIELDS) {
    target[key] ??= '';
  }
}

const DEFAULT_APP_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function isGodavariConfiguration(
  configuration: AnalyticsReporterConfiguration,
): configuration is GodavariAnalyticsReporterConfiguration {
  return 'provider' in configuration && configuration.provider === Provider.GODAVARI;
}

export class GodavariAnalyticsReporterImpl implements AnalyticsReporter {
  private static instance?: GodavariAnalyticsReporterImpl;
  private logger?: Logger;
  private configuration?: GodavariAnalyticsReporterConfiguration;
  private metadataStore = new GodavariMetadataStore();
  private playback = new GodavariPlaybackState();
  private readonly telemetry = new GodavariHeartbeatTelemetry();
  private pulseController?: GodavariPulseController;
  private videoEndReported = false;
  private initialized = false;
  private appSessionStarted = false;
  private backgroundedAt?: number;
  private exitHandlersRegistered = false;

  static getInstance(): GodavariAnalyticsReporterImpl {
    GodavariAnalyticsReporterImpl.instance ??= new GodavariAnalyticsReporterImpl();
    return GodavariAnalyticsReporterImpl.instance;
  }

  get provider(): Provider {
    return Provider.GODAVARI;
  }

  initialize(configuration: GodavariAnalyticsReporterConfiguration): Promise<void>;
  initialize(configuration: AnalyticsReporterConfiguration): Promise<void> {
    try {
      this.initializeReporter(configuration);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private initializeReporter(configuration: AnalyticsReporterConfiguration): void {
    if (!isGodavariConfiguration(configuration)) {
      throw new Error('A Godavari provider configuration is required.');
    }
    const godavariConfiguration = configuration;

    resolveGodavariPulseConfiguration(godavariConfiguration);
    const frontend = validateGodavariSdk();

    this.endPlaybackSession();
    this.unregisterExitHandlers();
    let logger = godavariConfiguration.logger;
    logger ??= createLogger(LoggerLevel.OFF, TAG_NAME);
    this.logger = logger;
    this.configuration = {
      ...godavariConfiguration,
      playbackMetadata: { ...godavariConfiguration.playbackMetadata },
    };
    this.metadataStore = new GodavariMetadataStore(
      buildApplicationMetadata(this.configuration, logger),
      godavariConfiguration.playbackMetadata,
      logger,
    );
    this.playback = new GodavariPlaybackState();
    this.appSessionStarted = false;
    this.backgroundedAt = undefined;

    configureGodavariFrontend(frontend, godavariConfiguration);
    this.initialized = true;
  }

  reportStartupObservation(observation: PlaybackStartupObservation): void {
    if (!this.isInitialized() || !isPlaybackStartupObservation(observation)) return;
    this.handlePulseObservation(toGodavariStartupObservation(observation));
  }

  reportPlaybackEndIntent(reason: PlaybackEndReason): void {
    if (!this.isInitialized()) return;
    const mapped = toGodavariEndReason(reason);
    if (mapped) this.metadataStore.apply({ custom_tags: { end_reason: mapped } });
  }

  reportEvent(...args: GodavariObservationArgs): void;
  reportEvent(eventName: GodavariMetadataEvent | GodavariLifecycleObservation): void;
  reportEvent(eventName: GodavariMetadataEvent, metadata?: GodavariEventInput): void;
  reportEvent(eventName: string, input: object = {}): void {
    if (!this.isInitialized()) {
      this.logger?.warn('Godavari reporter is not initialized.');
      return;
    }
    eventName = canonicalGodavariEventName(eventName);
    if (this.handleSpecialEvent(eventName, input)) return;
    this.handleMappedEvent(eventName, input);
  }

  private handleSpecialEvent(eventName: string, input: object): boolean {
    if (eventName === 'Pulse') {
      this.handlePulseObservation(input);
      return true;
    }
    if (eventName === 'BufferHealthSample') {
      this.handleBufferHealthSample(input);
      return true;
    }
    if (eventName === 'NetworkRequest' || eventName === 'NetworkResponse') {
      this.handleNetworkObservation(input, eventName);
      return true;
    }
    return false;
  }

  private handleBufferHealthSample(input: object): void {
    if (
      this.playback.phase === 'playing' &&
      !this.playback.adActive &&
      'position' in input &&
      'bufferedAhead' in input &&
      typeof input.position === 'number' &&
      typeof input.bufferedAhead === 'number'
    ) {
      this.telemetry.sampleBuffer(input.position, input.bufferedAhead);
    }
  }

  private handleNetworkObservation(input: object, eventName: 'NetworkRequest' | 'NetworkResponse'): void {
    if (eventName === 'NetworkResponse' && this.playback.phase !== 'ended' && !this.playback.adActive) {
      this.telemetry.recordNetwork(input as NetworkRequestObservation);
    }
    this.queueNetworkPulse(input, eventName === 'NetworkRequest' ? 'RequestStart' : 'ResponseEnd');
  }

  private handleMappedEvent(eventName: string, input: object): void {
    const metadata = normalizeGodavariEvent(input, this.logger);
    if ('end_reason' in input && this.isEndReason(input.end_reason)) {
      metadata.custom_tags = { ...metadata.custom_tags, end_reason: input.end_reason };
    }
    const playerState = STARTUP_PLAYER_STATES.get(eventName);
    if (playerState && this.playback.phase === 'startup') this.playback.setPlayerState(playerState);

    const detailEvent = VIDEO_DETAIL_EVENTS.get(eventName);
    if (detailEvent) {
      this.playback.setPlayerState(detailEvent.playerState);
      this.queueVideoEvent({ event_event_type: detailEvent.eventType }, metadata);
      return;
    }

    const adDetailEvent = AD_DETAIL_EVENTS.get(eventName);
    if (adDetailEvent) {
      this.queueAdEvent(adDetailEvent, metadata);
      return;
    }
    const startupStage = STARTUP_PULSE_EVENTS.get(eventName);
    if (startupStage) {
      this.queueStartupPulse(startupStage);
    }

    this.handleEvent(eventName, metadata);
  }

  private handleEvent(eventName: string, metadata: GodavariEventMetadata): void {
    switch (eventName) {
      case ApplicationEvent.APP_START:
        this.startApplicationSession(metadata);
        break;
      case ApplicationEvent.APP_BACKGROUND:
        this.backgroundedAt ??= Date.now();
        if (this.playback.connector) {
          this.sendEvent('videoSessionAppBackGround', metadata);
        }
        break;
      case ApplicationEvent.APP_FOREGROUND:
        this.rotateGodavariSessionIfExpired();
        if (this.playback.connector) {
          this.sendEvent('videoSessionAppForeGround', metadata);
        }
        break;
      case PlaybackEvent.PLAYBACK_REQUEST:
        this.startPlaybackSession(metadata);
        break;
      case 'ManifestParsing':
      case 'ManifestParsed':
      case PlaybackEvent.PLAYBACK_START:
        break;
      case PlaybackEvent.PLAYBACK_PREPARED:
      case 'VideoAttempt':
        this.reportVideoAttempt(metadata);
        break;
      case PlaybackEvent.PLAYBACK_STARTED:
        this.handlePlaybackStarted(metadata);
        break;
      case PlaybackEvent.PLAYBACK_SEEK_START:
        this.handleSeekStart(metadata);
        break;
      case PlaybackEvent.PLAYBACK_SEEK_COMPLETE:
        this.handleSeekComplete(metadata);
        break;
      case PlaybackEvent.PLAYBACK_VARIANT_CHANGE:
        this.queueVariantChange(metadata);
        break;
      case PlaybackEvent.PLAYBACK_AUDIO_LANGUAGE_CHANGE:
        this.queueLanguageChange('audio', metadata);
        break;
      case PlaybackEvent.PLAYBACK_SUBTITLE_LANGUAGE_CHANGE:
        this.queueLanguageChange('subtitle', metadata);
        break;
      case PlaybackEvent.PLAYBACK_METRIC:
      case PlaybackEvent.PLAYBACK_INPROGRESS:
      case AdPlaybackEvent.AD_PLAYBACK_METRIC:
        this.applyEventMetadata(metadata);
        break;
      case PlaybackEvent.PLAYBACK_COMPLETE:
        this.handlePlaybackComplete(metadata);
        break;
      case PlaybackEvent.PLAYBACK_END:
        this.endPlaybackSession(metadata);
        break;
      case PlaybackEvent.PLAYBACK_ERROR:
        this.handlePlaybackError(metadata);
        break;
      case AdPlaybackEvent.AD_START:
        this.startAd(metadata);
        break;
      case AdPlaybackEvent.AD_ATTEMPT:
        this.reportAdAttempt(metadata);
        break;
      case AdPlaybackEvent.AD_IMPRESSION:
        this.ensureAdAttempt(metadata);
        this.sendEvent('adsPlayImpression', {
          ...metadata,
          ...toGodavariAdMetadata(metadata, this.logger),
        });
        break;
      case AdPlaybackEvent.AD_END:
        this.endAd(metadata);
        break;
      case AdPlaybackEvent.AD_SKIP:
        this.queueAdEvent('heart_beat_ads_event_ad_skip', metadata);
        this.endAd(metadata);
        break;
      case AdPlaybackEvent.AD_SKIP_AFC_POLICY:
        // A skipped break never starts an ad, so it needs its own vendor session (ads-sid / ads-sst).
        this.applyAdPosition(metadata);
        // No ad plays for a skipped break, so it must not inherit the previous ad's watch time or session id.
        this.applyEventMetadata({ ads_length_watched: 0, ads_watch_time_duration: 0, ads_session_id: '' });
        this.ensureAdAttempt(metadata);
        this.queueAdEvent('heart_beat_ad_skip_true_view', metadata, true);
        this.flushAdEvents();
        this.playback.setAdAttempted(false);
        break;
      case AdPlaybackEvent.AD_ERROR:
        this.handleAdError(metadata);
        break;
      case 'vmaprequeststart':
        this.sendEvent('adsVmapRequestStart', {
          vmap_prefetch: '0',
          ...metadata,
        });
        break;
      case 'vmaprequestend':
        this.sendEvent('adsVmapRequestEnd', {
          vmap_prefetch: '0',
          vmap_success: 'true',
          ...metadata,
        });
        break;
      case 'AdHeartbeat':
        this.applyEventMetadata(metadata);
        this.flushAdEvents();
        break;
      default:
        this.logger?.log(`Ignoring unsupported Godavari event: ${eventName}`);
    }
  }

  reportMetadata(metadata: GodavariMetadataInput): void;
  reportMetadata(metadata: object): void {
    this.metadataStore.apply(metadata);
  }

  private applyEventMetadata(metadata: GodavariEventMetadata): void {
    this.metadataStore.applyNormalized(pickGodavariMetadata(metadata));
  }

  getApplicationSessionId(): string | undefined {
    return getGodavariApplicationSessionId();
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  reset(): void {
    this.endPlaybackSession();
    this.unregisterExitHandlers();
    this.initialized = false;
    this.configuration = undefined;
    this.metadataStore = new GodavariMetadataStore({}, {}, this.logger);
    this.playback = new GodavariPlaybackState();
    this.appSessionStarted = false;
    this.backgroundedAt = undefined;
  }

  private handlePlaybackStarted(metadata: GodavariEventMetadata): void {
    if (this.playback.phase === 'startup' && this.playback.connector) {
      this.playback.setPlayerState('Playing');
      this.pulseController?.onEvent(6);
      this.sendEvent('videoPlay', metadata);
      this.playback.markPlaying();
    }
  }

  private handleSeekStart(metadata: GodavariEventMetadata): void {
    this.playback.recordSeekStart(() => this.heartbeatSnapshot(metadata));
  }

  private handleSeekComplete(metadata: GodavariEventMetadata): void {
    if (!this.playback.recordSeekEnd(() => this.heartbeatSnapshot(metadata))) {
      this.logger?.warn('Godavari seek completed without a matching seek start.');
    }
  }

  private handlePlaybackComplete(metadata: GodavariEventMetadata): void {
    if (this.playback.connector && this.playback.phase !== 'ended') {
      this.applyEventMetadata(metadata);
      this.sampleDroppedFrames();
      this.playback.markEnded();
      this.endAd({});
      this.pulseController?.finish();
      this.sendEvent('videoEnd', metadata);
      this.videoEndReported = true;
    }
  }

  private handlePlaybackError(metadata: GodavariEventMetadata): void {
    if (!this.playback.connector) {
      this.logger?.warn('Ignoring Godavari playback error without an active playback session.');
      return;
    }
    this.applyEventMetadata(metadata);
    const latest = this.playback.latestPulseStage;
    if (latest && this.playback.phase === 'startup') {
      this.queueStartupPulse({ ...latest, pulse_micro_steps: 'ResponseError' });
    }
    this.pulseController?.finish();
    this.sampleDroppedFrames();
    this.playback.markEnded();
    this.sendEvent('videoError', withGodavariErrorFields(metadata));
  }

  /**
   * The vendor rejects ad beacons without ads-pos, and failures can occur before any ad metadata is applied.
   * A previous break's position is replaced whenever the incoming metadata names its own break position.
   */
  private applyAdPosition(metadata: GodavariEventMetadata): void {
    const { adBreakInfo, adBreak } = metadata as { adBreakInfo?: any; adBreak?: any };
    const isUsable = (position?: string) => !!position && position !== AdContentTimePosition.UNKNOWN;
    const carriesPosition =
      isUsable(adBreakInfo?.contentTimePosition) || isUsable(adBreakInfo?.position) || isUsable(adBreak?.position);
    if (carriesPosition || !this.metadataStore.playback.ads_position) {
      this.applyEventMetadata({ ads_position: toGodavariAdMetadata(metadata, this.logger).ads_position });
    }
  }

  private handleAdError(metadata: GodavariEventMetadata): void {
    this.applyAdPosition(metadata);
    this.queueStartupPulse({
      pulse_start_steps: 'AdPlay',
      pulse_micro_steps: 'ResponseError',
      pulse_player_state: 'Ad',
    });
    this.flushPulseEvents();
    this.flushAdEvents();
    this.sendEvent('adsError', withGodavariErrorFields(metadata));
    this.endAd(metadata);
  }

  private startApplicationSession(metadata: GodavariEventMetadata): void {
    if (this.appSessionStarted) {
      return;
    }
    this.ensureApplicationIds();
    this.registerExitHandlers();
    const payload = buildApplicationPayload(this.metadataStore.application, metadata, Date.now(), this.logger);
    startGodavariApplication(payload);
    this.appSessionStarted = true;
    this.backgroundedAt = undefined;
  }

  /**
   * The vendor builds as-si itself from as-did, so the device id must be stable. The Godavari
   * device id (as-gvuid) persists, while the Godavari session id (as-gvsid) lives for one page load.
   */
  private ensureApplicationIds(): void {
    const application = this.metadataStore.application;
    const gvuid = application.app_gv_device_id || getOrCreateGodavariGvuid();
    this.metadataStore.apply({
      app_device_id: getOrCreateGodavariDeviceId() || application.app_device_id,
      app_gv_device_id: gvuid,
      app_gv_session_id: application.app_gv_session_id || createGodavariSessionId(gvuid)
    });
  }

  /** Returning from the background after the idle timeout starts a new Godavari session id. */
  private rotateGodavariSessionIfExpired(): void {
    const backgroundedAt = this.backgroundedAt;
    this.backgroundedAt = undefined;
    if (!this.appSessionStarted || backgroundedAt === undefined) return;
    const timeout = this.configuration?.appSessionTimeout ?? DEFAULT_APP_SESSION_TIMEOUT_MS;
    if (Date.now() - backgroundedAt < timeout) return;
    const gvuid = this.metadataStore.application.app_gv_device_id || getOrCreateGodavariGvuid();
    this.metadataStore.apply({ app_gv_session_id: createGodavariSessionId(gvuid) });
  }

  private registerExitHandlers(): void {
    if (this.exitHandlersRegistered || typeof window === 'undefined') return;
    // Registered before the vendor's own unload listener so video and ad ends precede AppSessionEnd.
    window.addEventListener('beforeunload', this.handleExit);
    window.addEventListener('pagehide', this.handleExit);
    this.exitHandlersRegistered = true;
  }

  private unregisterExitHandlers(): void {
    if (!this.exitHandlersRegistered) return;
    window.removeEventListener('beforeunload', this.handleExit);
    window.removeEventListener('pagehide', this.handleExit);
    this.exitHandlersRegistered = false;
  }

  /** Ends the active ad and video session so they are reported, with their own ids, on the next launch. */
  private readonly handleExit = (): void => {
    if (!this.playback.connector) return;
    const restoreDelivery = holdGodavariDelivery();
    try {
      this.endPlaybackSession();
    } catch (error) {
      this.logger?.warn(`Godavari exit handling failed: ${String(error)}`);
    } finally {
      restoreDelivery();
    }
  };

  private startPlaybackSession(metadata: GodavariEventMetadata): void {
    if (this.playback.connector) {
      this.logger?.warn('A Godavari playback session is already active.');
      return;
    }
    this.applyEventMetadata(metadata);
    if (this.metadataStore.playback.video_content_id === undefined) {
      this.logger?.warn('Godavari playback cannot start without a valid video_content_id.');
      return;
    }
    const configuration = this.requireConfiguration();
    const connector = createGodavariConnector(getDeviceInformation(configuration));
    this.playback.connector = connector;
    this.playback.initializeHistory(this.metadataStore.playback);
    const playback = this.playback;
    registerGodavariCallbacks(
      connector,
      { heartbeat: this.buildHeartbeatPayload, pulse: emptyGodavariBatch },
      () => this.playback === playback,
    );
    this.telemetry.reset();
    this.videoEndReported = false;
    this.pulseController = new GodavariPulseController(
      connector,
      configuration,
      this.buildPulsePayload,
      () => this.playback === playback,
    );
    this.sendEvent('videoSessionStart', metadata);
    this.pulseController.onEvent(1);
  }

  private endPlaybackSession(metadata: GodavariEventMetadata = {}): void {
    if (!this.playback.connector) {
      return;
    }
    this.applyEventMetadata(metadata);
    this.sampleDroppedFrames();
    this.playback.markEnded();
    this.endAd({});
    this.pulseController?.finish();
    if (!this.videoEndReported) {
      this.sendEvent('videoEnd', metadata);
      this.videoEndReported = true;
    }
    closeGodavariSession(this.playback.connector, () => this.sendEvent('videoSessionEnd', metadata));
    this.pulseController?.dispose();
    this.pulseController = undefined;
    this.telemetry.reset();
    this.playback = new GodavariPlaybackState();
    this.metadataStore.resetPlayback();
  }

  private payloadContext(): GodavariPayloadContext {
    return {
      configuration: this.requireConfiguration(),
      metadata: this.metadataStore.playback,
      applicationMetadata: this.metadataStore.application,
      logger: this.logger,
    };
  }

  private sendEvent<K extends keyof GodavariLifecyclePayloads>(method: K, metadata: GodavariEventMetadata = {}): void {
    this.sendConnectorPayload(method, () => {
      const payload: GodavariLifecyclePayload<K> = {
        ...buildCommonPayload(this.payloadContext(), this.lifecycleMetadata(method, metadata), Date.now()),
        ...GODAVARI_LIFECYCLE_HEADERS[method],
        event_data_list: undefined,
      };
      omitUnsetOptionalFields(payload);
      // Lifecycle payloads are the same keyed subset of the connector payload map.
      return payload as GodavariConnectorPayloads[K];
    });
  }

  private sendConnectorPayload<K extends keyof GodavariConnectorPayloads>(
    method: K,
    createPayload: () => GodavariConnectorPayloads[K],
  ): void {
    const connector = this.playback.connector;
    if (!connector) {
      this.logger?.warn(`Ignoring Godavari ${method} without an active playback session.`);
      return;
    }
    sendGodavariPayload(connector, method, createPayload);
  }

  private readonly buildHeartbeatPayload = (): GodavariBatchPayloads['videoSessionHeartbeat'] | GodavariEmptyBatch => {
    if (!this.playback.connector) {
      return emptyGodavariBatch();
    }
    this.sampleDroppedFrames();
    const measurements = this.telemetry.drain();
    const includeMeasurements =
      this.playback.phase === 'playing' || measurements.bufferHealth.length || measurements.networkActivity.length;
    const snapshot = this.heartbeatSnapshot();
    const batch = this.playback.takeVideoBatch(
      includeMeasurements
        ? [
          {
            ...snapshot,
            event_event_type: 'heart_beat_event_network_activity',
            event_network_activity: measurements.networkActivity,
          },
          {
            ...snapshot,
            event_event_type: 'heart_beat_event_buffer_health',
            event_buffer_health: measurements.bufferHealth,
          },
        ]
        : [],
    );
    return buildBatchPayload(this.payloadContext(), 'videoSessionHeartbeat', batch, Date.now());
  };

  private sampleDroppedFrames(): void {
    this.playback.recordDroppedFrames(this.metadataStore.playback.video_dropped_frames, () => this.heartbeatSnapshot());
  }

  private readonly buildPulsePayload = (): GodavariBatchPayloads['videoSessionPulse'] | GodavariEmptyBatch => {
    if (!this.playback.connector) {
      return emptyGodavariBatch();
    }
    return buildBatchPayload(this.payloadContext(), 'videoSessionPulse', this.playback.takePulseBatch(), Date.now());
  };

  private heartbeatSnapshot(metadata: GodavariEventMetadata = {}) {
    return buildHeartbeatFields(this.metadataStore.playback, metadata, Date.now(), this.logger);
  }

  private queueVideoEvent(details: GodavariVideoDetailInput, metadata: GodavariEventMetadata = {}): void {
    this.playback.recordVideoEvent(details, () => this.heartbeatSnapshot(metadata));
  }

  private queueVariantChange(metadata: GodavariEventMetadata): void {
    const to = this.playback.recordBitrateChange(metadata, () => this.heartbeatSnapshot(metadata));
    if (to === undefined) {
      this.logger?.warn('Godavari bitrate change requires finite previous and current bitrates.');
      return;
    }
    this.applyEventMetadata({ ...metadata, video_current_bitrate: to });
  }

  private queueLanguageChange(kind: 'audio' | 'subtitle', metadata: GodavariEventMetadata): void {
    const key = kind === 'audio' ? 'video_audio_language' : 'video_subtitle_language';
    const next =
      metadata[key] ?? metadata[kind === 'audio' ? PlaybackDataKey.AUDIO_LANGUAGE : PlaybackDataKey.SUBTITLE_LANGUAGE];
    if (!this.playback.recordLanguageChange(kind, next, () => this.heartbeatSnapshot(metadata))) {
      this.logger?.warn(`Godavari ${kind} change requires a language.`);
      return;
    }
    // The state operation validated the language before updating its history.
    this.applyEventMetadata({ [key]: next });
  }

  private reportVideoAttempt(metadata: GodavariEventMetadata): void {
    if (this.playback.connector && !this.playback.videoAttempted && this.playback.phase !== 'ended') {
      this.sendEvent('videoAttempt', metadata);
      this.playback.markVideoAttempted();
      this.pulseController?.onEvent(2);
    }
  }

  private queueNetworkPulse(input: object, microStep: 'RequestStart' | 'ResponseEnd'): void {
    const requestType = 'requestType' in input ? input.requestType : undefined;
    const timestamp = 'timestamp' in input ? input.timestamp : undefined;
    const stage = getNetworkPulseStage(requestType);
    if (!stage || typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
      this.logger?.warn('Ignoring invalid Godavari network observation.');
      return;
    }
    this.queueStartupPulse({ ...stage, pulse_micro_steps: microStep }, timestamp);
  }

  private queueStartupPulse(observation: GodavariPulseStage, timestamp = Date.now()): void {
    if (this.playback.recordStartupStage(observation, timestamp)) {
      this.pulseController?.resetForRetry();
      this.pulseController?.onEvent(1);
    }
  }

  private flushPulseEvents(): void {
    this.pulseController?.flush();
  }

  private queueAdEvent(eventType: GodavariAdDetailType, metadata: GodavariEventMetadata, force = false): void {
    this.playback.recordAdEvent(
      eventType,
      () =>
        this.heartbeatSnapshot({
          ...metadata,
          event_start_position: Number(
            metadata.event_start_position ?? this.metadataStore.playback.ads_length_watched ?? 0,
          ),
        }),
      force,
    );
  }

  private flushAdEvents(): void {
    if (this.playback.hasAdDetails) {
      this.sendConnectorPayload('adsHeartbeat', () =>
        buildBatchPayload(
          this.payloadContext(),
          'adsHeartbeat',
          { event_data_list: this.playback.takeAdDetails() },
          Date.now(),
        ),
      );
    }
  }

  private reportAdAttempt(metadata: GodavariEventMetadata): void {
    this.playback.setAdAttempted(true);
    this.pulseController?.onEvent(3);
    this.queueStartupPulse({ pulse_start_steps: 'AdPlay', pulse_micro_steps: 'NA', pulse_player_state: 'Ad' });
    this.applyEventMetadata({ ...toGodavariAdMetadata(metadata, this.logger) });
    this.sendEvent('adsAttempt', metadata);
  }

  /** Players may skip the loaded event; later ad beacons are rejected without the attempt's session fields. */
  private ensureAdAttempt(metadata: GodavariEventMetadata): void {
    if (!this.playback.adAttempted) {
      this.reportAdAttempt(metadata);
    }
  }

  private startAd(metadata: GodavariEventMetadata): void {
    if (!this.playback.beginAd()) {
      return;
    }
    this.ensureAdAttempt(metadata);
    this.queueStartupPulse({
      pulse_start_steps: 'AdPlay',
      pulse_micro_steps: 'RequestStart',
      pulse_player_state: 'Ad',
    });
    this.flushPulseEvents();
    this.pulseController?.onEvent(4);
    this.pulseController?.pauseForAd();
    this.playback.clearAdDetails();
    this.applyEventMetadata({ ads_length_watched: 0, ads_watch_time_duration: 0 });
    if ('adID' in metadata || 'adId' in metadata || 'durationMs' in metadata || 'duration' in metadata) {
      this.applyEventMetadata({ ...toGodavariAdMetadata(metadata, this.logger) });
    } else if (metadata.adSessionId) {
      this.applyEventMetadata({ ads_session_id: metadata.adSessionId });
    }
    this.sendEvent('adsPlay', metadata);
  }

  private endAd(metadata: GodavariEventMetadata): void {
    this.playback.setAdAttempted(false);
    if (!this.playback.adActive) {
      return;
    }
    this.applyEventMetadata(metadata);
    if (metadata.adSessionId) {
      this.applyEventMetadata({ ads_session_id: metadata.adSessionId });
    }
    this.queueStartupPulse({ pulse_start_steps: 'AdPlay', pulse_micro_steps: 'ResponseEnd', pulse_player_state: 'Ad' });
    this.flushAdEvents();
    this.sendEvent('adsEnd', {
      ...metadata,
      adend_event_initiator: 'player',
    });
    this.playback.finishAd();
    this.pulseController?.onEvent(5);
    this.pulseController?.resumeAfterAd();
  }

  private isEndReason(value: unknown): value is GodavariEndReason {
    return value === 'UserIntent' || value === 'AutoRetry' || value === 'Binge' || value === 'AssetClick';
  }

  private lifecycleMetadata(
    method: keyof GodavariLifecyclePayloads,
    metadata: GodavariEventMetadata,
  ): GodavariEventMetadata {
    if (method !== 'videoEnd' && method !== 'videoSessionAppBackGround') return metadata;
    const custom_tags = { ...metadata.custom_tags, playback_state: this.playback.playerState };
    if (method === 'videoEnd') {
      const candidate =
        metadata.custom_tags?.end_reason ??
        this.metadataStore.playback.custom_tags?.end_reason ??
        this.configuration?.customTags?.end_reason;
      if (candidate !== undefined && !this.isEndReason(candidate))
        this.logger?.warn('Ignoring invalid Godavari end_reason.');
      Object.assign(custom_tags, { end_reason: this.isEndReason(candidate) ? candidate : 'UserIntent' });
    }
    return { ...metadata, custom_tags };
  }

  private handlePulseObservation(input: object): void {
    const value = input as Partial<GodavariPulseObservation>;
    const microStep = value.pulse_micro_steps ?? 'NA';
    if (
      value.pulse_start_steps === undefined ||
      !GODAVARI_PULSE_STEPS.includes(value.pulse_start_steps) ||
      !GODAVARI_PULSE_MICRO_STEPS.includes(microStep) ||
      value.pulse_player_state === undefined ||
      !GODAVARI_PULSE_PLAYER_STATES.includes(value.pulse_player_state) ||
      (microStep === 'ChunkNumber' && value.pulse_start_steps !== 'ChunkDownload') ||
      ((value.pulse_start_steps === 'ChunkDownload' ||
        value.pulse_start_steps === 'ConcurrencyCheck' ||
        value.pulse_start_steps === 'AdPlay') &&
        (microStep === 'ParsingStart' || microStep === 'ParsingEnd')) ||
      (value.pulse_player_retry !== undefined && value.pulse_player_retry !== 0 && value.pulse_player_retry !== 1) ||
      (value.timestamp !== undefined && (typeof value.timestamp !== 'number' || !Number.isFinite(value.timestamp))) ||
      (value.requestId !== undefined && typeof value.requestId !== 'string') ||
      (value.autoPlayAllowed !== undefined && typeof value.autoPlayAllowed !== 'boolean') ||
      (value.retryFromStart !== undefined && typeof value.retryFromStart !== 'boolean')
    ) {
      this.logger?.warn('Ignoring invalid Godavari Pulse observation.');
      return;
    }
    if (value.retryFromStart && this.playback.phase === 'ended') {
      if (!this.playback.restartStartupForRetry()) return;
      // The vendor suppresses later errors and stops heartbeats after startup failure.
      this.playback.connector?.updatePlayerFlagStatus?.();
      this.playback.connector?.startHeartbeat?.();
    }
    if (this.playback.phase !== 'startup' || !this.playback.connector) return;
    if (value.autoPlayAllowed !== undefined) {
      this.metadataStore.apply({
        custom_tags: { ...this.metadataStore.playback.custom_tags, AutoPlayAllowed: value.autoPlayAllowed },
      });
    }
    const observation = {
      pulse_start_steps: value.pulse_start_steps,
      pulse_micro_steps: microStep,
      pulse_player_state: value.pulse_player_state,
      pulse_player_retry: value.pulse_player_retry,
    } as GodavariPulseStage;
    const reset = this.playback.recordStartupStage(observation, value.timestamp ?? Date.now(), {
      requestId: value.requestId,
      retryFromStart: value.retryFromStart,
    });
    if (reset) {
      this.pulseController?.resetForRetry();
      this.pulseController?.onEvent(1);
    }
  }

  private requireConfiguration(): GodavariAnalyticsReporterConfiguration {
    if (!this.configuration) {
      throw new Error('Godavari reporter configuration is unavailable.');
    }
    return this.configuration;
  }
}
